/**
 * Mappers for reconciling UI types with Database types
 *
 * These functions transform between:
 * - ServiceTicketRow (database schema - snake_case)
 * - ServiceTicket    (UI schema - camelCase)
 */

import type {
  ServiceTicket,
  ServiceTicketActivity,
  ServiceTicketStatus,
  ServiceTicketPriority,
  ServiceTicketCategory,
} from "@/data/serviceTicketTypes";
import type { ServiceTicketRow } from "@/services/service-ticket.service";

// ── DB → UI ───────────────────────────────────────────────────────────────
// Pass an optional resolved jobNumber to avoid a join — the service enriches
// tickets with job_numbers after the initial mapping.

export function toUiServiceTicket(db: ServiceTicketRow, jobNumber?: string): ServiceTicket {
  return {
    id: db.ticket_id,
    jobId: jobNumber ?? db.job_id ?? "",
    customerId: db.customer_id ?? "",
    customerName: db.customer_name,
    customerPhone: db.customer_phone ?? "",
    customerEmail: db.customer_email ?? "",
    address: db.address,
    category: db.category as ServiceTicketCategory,
    priority: db.priority as ServiceTicketPriority,
    status: db.status as ServiceTicketStatus,
    subject: db.subject,
    description: db.description ?? "",
    resolution: db.resolution ?? undefined,
    resolvedAt: db.resolved_at ? db.resolved_at.split("T")[0] : undefined,
    resolvedBy: db.resolved_by ?? undefined,
    assignedTo: db.assigned_to ?? "",
    createdBy: db.created_by ?? "",
    createdAt: db.created_at.split("T")[0],
    updatedAt: db.updated_at.split("T")[0],
    activities: Array.isArray(db.activities) ? db.activities : [],
    scheduledDate: db.scheduled_date ?? undefined,
    scheduledTimeSlot: db.scheduled_time_slot ?? undefined,
    notes: db.notes ?? "",
  };
}

// ── UI → DB (create) ──────────────────────────────────────────────────────
// Note: job_id must be the UUID FK. Pass the resolved UUID as jobUuid.

export function toDbServiceTicketCreate(
  ui: ServiceTicket,
  jobUuid?: string
): Omit<ServiceTicketRow, "id" | "created_at" | "updated_at"> {
  return {
    ticket_id: ui.id,
    job_id: jobUuid ?? ui.jobId ?? null,
    customer_id: ui.customerId || null,
    customer_name: ui.customerName,
    customer_phone: ui.customerPhone || null,
    customer_email: ui.customerEmail || null,
    address: ui.address,
    category: ui.category,
    priority: ui.priority,
    status: ui.status,
    subject: ui.subject,
    description: ui.description || null,
    resolution: ui.resolution || null,
    resolved_at: ui.resolvedAt ? `${ui.resolvedAt}T00:00:00Z` : null,
    resolved_by: ui.resolvedBy || null,
    assigned_to: ui.assignedTo || null,
    created_by: ui.createdBy || null,
    scheduled_date: ui.scheduledDate ?? null,
    scheduled_time_slot: ui.scheduledTimeSlot ?? null,
    activities: (ui.activities ?? []) as ServiceTicketActivity[],
    notes: ui.notes || null,
  };
}

// ── UI → DB (update) ──────────────────────────────────────────────────────

export function toDbServiceTicketUpdate(
  ui: Partial<ServiceTicket>,
  jobUuid?: string
): Partial<Omit<ServiceTicketRow, "id" | "created_at" | "updated_at">> {
  const patch: Partial<Omit<ServiceTicketRow, "id" | "created_at" | "updated_at">> = {};

  if (jobUuid !== undefined || ui.jobId !== undefined)
    patch.job_id = jobUuid ?? ui.jobId ?? null;
  if (ui.customerId !== undefined) patch.customer_id = ui.customerId || null;
  if (ui.customerName !== undefined) patch.customer_name = ui.customerName;
  if (ui.customerPhone !== undefined) patch.customer_phone = ui.customerPhone || null;
  if (ui.customerEmail !== undefined) patch.customer_email = ui.customerEmail || null;
  if (ui.address !== undefined) patch.address = ui.address;
  if (ui.category !== undefined) patch.category = ui.category;
  if (ui.priority !== undefined) patch.priority = ui.priority;
  if (ui.status !== undefined) patch.status = ui.status;
  if (ui.subject !== undefined) patch.subject = ui.subject;
  if (ui.description !== undefined) patch.description = ui.description || null;
  if (ui.resolution !== undefined) patch.resolution = ui.resolution || null;
  if (ui.resolvedAt !== undefined)
    patch.resolved_at = ui.resolvedAt ? `${ui.resolvedAt}T00:00:00Z` : null;
  if (ui.resolvedBy !== undefined) patch.resolved_by = ui.resolvedBy || null;
  if (ui.assignedTo !== undefined) patch.assigned_to = ui.assignedTo || null;
  if (ui.scheduledDate !== undefined) patch.scheduled_date = ui.scheduledDate ?? null;
  if (ui.scheduledTimeSlot !== undefined) patch.scheduled_time_slot = ui.scheduledTimeSlot ?? null;
  if (ui.activities !== undefined) patch.activities = (ui.activities ?? []) as ServiceTicketActivity[];
  if (ui.notes !== undefined) patch.notes = ui.notes || null;

  return patch;
}

// ── Batch ─────────────────────────────────────────────────────────────────

export function toUiServiceTickets(rows: ServiceTicketRow[]): ServiceTicket[] {
  return rows.map((r) => toUiServiceTicket(r));
}
